import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { adminClient } from "./api";

const STATUSES = ["open", "reviewing", "approved", "denied", "resolved"];
const COLOR = { open: "bg-amber-100 text-amber-900", reviewing: "bg-sky-100 text-sky-900", approved: "bg-emerald-100 text-emerald-900", denied: "bg-red-100 text-red-800", resolved: "bg-zinc-200 text-zinc-700" };
const fileUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export function ClaimRow({ claim, onChanged, showCustomer = false }) {
  const [status, setStatus] = useState(claim.status);
  const [note, setNote] = useState(claim.resolution_note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient.put(`/admin/claims/${claim.id}`, { status, resolution_note: note, notify: true });
      toast.success(`Claim ${status}${data.email?.status === "sent" ? " · customer emailed" : ""}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update claim"); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-md border-2 p-3 space-y-2 ${claim.overdue ? "border-red-400 bg-red-50/40" : "border-zinc-200"}`} data-testid={`drawer-claim-${claim.id}`}>
      {showCustomer && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-display font-black text-[#0C2E2B]">{claim.customer_name} · <span className="font-mono-data text-sm">{claim.ref_id}</span></div>
            <div className="text-xs text-zinc-600">{claim.customer_phone && <a href={`tel:${claim.customer_phone}`} className="underline">{claim.customer_phone}</a>} · <a href={`mailto:${claim.customer_email}`} className="underline">{claim.customer_email}</a></div>
          </div>
          {claim.awaiting_response && (
            <span data-testid={`claim-deadline-${claim.id}`} className={`text-[11px] font-bold px-2 py-1 rounded ${claim.overdue ? "bg-red-600 text-white" : claim.hours_left < 12 ? "bg-amber-400 text-[#0C2E2B]" : "bg-zinc-100 text-zinc-700"}`}>
              {claim.overdue ? `Overdue by ${Math.abs(Math.round(claim.hours_left))}h` : `Respond within ${Math.round(claim.hours_left)}h`}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-zinc-500">{new Date(claim.created_at).toLocaleString()} · prefers <b>{claim.contact_preference}</b>{!claim.within_window && <span className="ml-1 text-red-700 font-bold">· filed late</span>}</div>
        <Badge data-testid={`drawer-claim-status-${claim.id}`} className={`text-[10px] ${COLOR[claim.status]} hover:${COLOR[claim.status]}`}>{claim.status}</Badge>
      </div>
      <p className="text-sm text-[#0C2E2B]">{claim.description}</p>
      {claim.photo_urls?.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5">
          {claim.photo_urls.map((u, i) => <a key={u} href={fileUrl(u)} target="_blank" rel="noreferrer" className="aspect-square rounded overflow-hidden border border-zinc-200"><img src={fileUrl(u)} alt={`Claim ${i + 1}`} className="w-full h-full object-cover" /></a>)}
        </div>
      )}
      <div className="flex gap-2">
        <select data-testid={`drawer-claim-select-${claim.id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border-2 border-zinc-200 text-xs font-bold px-2 bg-white">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Textarea data-testid={`drawer-claim-note-${claim.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={1} placeholder="Note to customer (e.g. $40 refund sent via Zelle)" className="rounded-md text-xs flex-1 min-h-9" />
        <Button data-testid={`drawer-claim-save-${claim.id}`} size="sm" disabled={busy} onClick={save} className="h-9 rounded-md text-xs font-bold bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90">Update</Button>
      </div>
    </div>
  );
}

export default function ClaimsBlock({ booking, onChanged }) {
  const claims = booking.claims || [];
  if (claims.length === 0 && !booking.delivery_receipt) return null;
  return (
    <div className="space-y-3" data-testid="drawer-claims-block">
      {booking.delivery_receipt && (
        <div className="text-xs text-zinc-600 flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="drawer-receipt-summary">
          <span>Proof of delivery signed by <b>{booking.delivery_receipt.signed_by}</b> · {new Date(booking.delivery_receipt.signed_at).toLocaleString()}</span>
          <a href={`/receipt/${booking.ref_id}`} target="_blank" rel="noreferrer" className="underline font-bold text-[#0C2E2B]">View</a>
        </div>
      )}
      {claims.length === 0 ? <p className="text-xs text-zinc-500">No damage claims.</p> : claims.map((c) => <ClaimRow key={c.id} claim={c} onChanged={onChanged} />)}
    </div>
  );
}
